import { Command } from 'commander';
import pc from 'picocolors';
import { readFileSync, writeFileSync, existsSync } from 'fs';
import { join } from 'path';
import { getErrorMessage } from '../utils/error-type-guards';

export const configCommand = new Command('config')
  .description('Manage Vibecode DevTools configuration')
  .addCommand(
    new Command('list')
      .alias('ls')
      .description('List all configuration values')
      .action(listConfig)
  )
  .addCommand(
    new Command('get')
      .description('Get a configuration value')
      .argument('<key>', 'configuration key to get')
      .action(getConfig)
  )
  .addCommand(
    new Command('set')
      .description('Set a configuration value')
      .argument('<key>', 'configuration key to set')
      .argument('<value>', 'value to set')
      .action(setConfig)
  )
  .addCommand(
    new Command('reset')
      .description('Reset configuration to defaults')
      .option('-y, --yes', 'skip confirmation')
      .action(resetConfig)
  );

function getConfigPath(): string {
  const configFiles = [
    'vibecode.config.ts',
    'vibecode.config.js',
    'vibecode.config.mjs',
  ];

  for (const file of configFiles) {
    const path = join(process.cwd(), file);
    if (existsSync(path)) {
      return path;
    }
  }

  throw new Error('No vibecode.config file found. Run "vibecode-devtools init" first.');
}

async function listConfig() {
  try {
    const configPath = getConfigPath();
    const configContent = readFileSync(configPath, 'utf-8');
    
    console.log(pc.bold('\nVibecode DevTools Configuration:\n'));
    console.log(pc.dim(`Config file: ${configPath}\n`));
    
    // Simple parsing for display (not evaluation)
    const lines = configContent.split('\n');
    let inConfig = false;
    
    lines.forEach(line => {
      if (line.includes('defineConfig({')) {
        inConfig = true;
      } else if (inConfig && line.includes('});')) {
        inConfig = false;
      } else if (inConfig && line.trim() && !line.trim().startsWith('//')) {
        console.log(pc.cyan('  ' + line.trim()));
      }
    });
    
    console.log('');
  } catch (error) {
    console.error(pc.red('Error:'), getErrorMessage(error));
    process.exit(1);
  }
}

async function getConfig(key: string) {
  try {
    const configPath = getConfigPath();
    const configContent = readFileSync(configPath, 'utf-8');
    
    // Simple key search (this is a basic implementation)
    const keyPattern = new RegExp(`${key}:\\s*(.+?)[,\\n}]`, 's');
    const match = configContent.match(keyPattern);
    
    if (match) {
      console.log(pc.green(`${key}:`), match[1].trim());
    } else {
      console.log(pc.yellow(`Configuration key "${key}" not found`));
    }
  } catch (error) {
    console.error(pc.red('Error:'), getErrorMessage(error));
    process.exit(1);
  }
}

async function setConfig(key: string, value: string) {
  console.log(pc.yellow('\nNote: Manual editing of the config file is recommended for complex values.'));
  console.log(pc.dim(`Setting ${key} = ${value}`));
  
  try {
    const configPath = getConfigPath();
    console.log(pc.green('\n✅ Please edit your config file directly:'));
    console.log(pc.cyan(`   ${configPath}`));
  } catch (error) {
    console.error(pc.red('Error:'), getErrorMessage(error));
    process.exit(1);
  }
}

async function resetConfig(options: any) {
  if (!options.yes) {
    console.log(pc.yellow('\n⚠️  This will reset your configuration to defaults.'));
    console.log(pc.dim('Your current configuration will be backed up.'));
    
    const inquirer = await import('inquirer');
    const { confirm } = await inquirer.default.prompt([{
      type: 'confirm',
      name: 'confirm',
      message: 'Are you sure you want to reset the configuration?',
      default: false,
    }]);
    
    if (!confirm) {
      console.log(pc.dim('\nOperation cancelled.'));
      return;
    }
  }
  
  try {
    const configPath = getConfigPath();
    const backupPath = configPath + '.backup';
    
    // Create backup
    const configContent = readFileSync(configPath, 'utf-8');
    writeFileSync(backupPath, configContent);
    console.log(pc.dim(`\nBacked up current config to: ${backupPath}`));
    
    // Reset to default
    const defaultConfig = `import { defineConfig } from '@vibecode/devtools';

export default defineConfig({
  scanPaths: ['./docs', './README.md'],
  include: ['**/*.md', '**/*.mdx'],
  exclude: ['**/node_modules/**', '**/.next/**'],
  features: {
    tasks: true,
    ai: true,
    realtime: true,
    commandPalette: true,
  },
  taskPatterns: {
    todo: true,
    fixme: true,
    hack: true,
    note: true,
  },
});
`;
    
    writeFileSync(configPath, defaultConfig);
    console.log(pc.green('\n✅ Configuration reset to defaults'));
  } catch (error) {
    console.error(pc.red('Error:'), getErrorMessage(error));
    process.exit(1);
  }
}